import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { EffortOverview } from "../components/EffortOverview";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, Clock, Plus, Trash2 } from "lucide-react";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, parseISO, isSameDay } from "date-fns";

export default function TimesheetsPage() {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Weekly grid data structure
  const [weeklyData, setWeeklyData] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  useEffect(() => {
    fetchData();
  }, [currentWeek]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsRes, entriesRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers }),
        axios.get(`${API}/timesheets`, { 
          headers,
          params: {
            start_date: format(weekStart, "yyyy-MM-dd"),
            end_date: format(weekEnd, "yyyy-MM-dd")
          }
        })
      ]);
      
      setProjects(projectsRes.data.filter(p => p.status === "active"));
      setEntries(entriesRes.data);
      
      // Build weekly grid from entries
      buildWeeklyGrid(entriesRes.data, projectsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const buildWeeklyGrid = (entries, projects) => {
    // Group entries by project
    const projectHours = {};
    
    entries.forEach(entry => {
      if (!projectHours[entry.project_id]) {
        projectHours[entry.project_id] = {
          project_id: entry.project_id,
          project_name: entry.project_name,
          client_name: entry.client_name,
          hours: {}
        };
      }
      projectHours[entry.project_id].hours[entry.date] = {
        hours: entry.hours,
        description: entry.description,
        id: entry.id
      };
    });
    
    setWeeklyData(Object.values(projectHours));
  };

  const addProjectRow = () => {
    setWeeklyData([
      ...weeklyData,
      {
        project_id: "",
        project_name: "",
        client_name: "",
        hours: {},
        isNew: true
      }
    ]);
  };

  const removeProjectRow = (index) => {
    const newData = [...weeklyData];
    newData.splice(index, 1);
    setWeeklyData(newData);
  };

  const updateProjectSelection = (index, projectId) => {
    const project = projects.find(p => p.id === projectId);
    const newData = [...weeklyData];
    newData[index] = {
      ...newData[index],
      project_id: projectId,
      project_name: project?.name || "",
      client_name: project?.client_name || ""
    };
    setWeeklyData(newData);
  };

  const updateHours = (rowIndex, dateStr, hours) => {
    const newData = [...weeklyData];
    if (!newData[rowIndex].hours[dateStr]) {
      newData[rowIndex].hours[dateStr] = { hours: 0, description: "" };
    }
    newData[rowIndex].hours[dateStr].hours = parseFloat(hours) || 0;
    setWeeklyData(newData);
  };

  const saveTimesheets = async () => {
    setSaving(true);
    try {
      // Process each row
      for (const row of weeklyData) {
        if (!row.project_id) continue;
        
        for (const [dateStr, data] of Object.entries(row.hours)) {
          if (data.hours > 0) {
            if (data.id) {
              // Update existing entry
              await axios.put(`${API}/timesheets/${data.id}`, {
                project_id: row.project_id,
                date: dateStr,
                hours: data.hours,
                description: data.description || ""
              }, { headers });
            } else {
              // Create new entry
              await axios.post(`${API}/timesheets`, {
                project_id: row.project_id,
                date: dateStr,
                hours: data.hours,
                description: data.description || ""
              }, { headers });
            }
          } else if (data.id && data.hours === 0) {
            // Delete entry if hours set to 0
            await axios.delete(`${API}/timesheets/${data.id}`, { headers });
          }
        }
      }
      
      toast.success("Timesheet saved successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to save timesheet");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const getTotalForRow = (row) => {
    return Object.values(row.hours).reduce((sum, h) => sum + (h.hours || 0), 0);
  };

  const getTotalForDay = (dateStr) => {
    return weeklyData.reduce((sum, row) => {
      return sum + (row.hours[dateStr]?.hours || 0);
    }, 0);
  };

  const getWeekTotal = () => {
    return weeklyData.reduce((sum, row) => sum + getTotalForRow(row), 0);
  };

  // Get already selected project IDs (excluding current row)
  const getAvailableProjects = (currentIndex) => {
    const selectedIds = weeklyData
      .filter((_, i) => i !== currentIndex)
      .map(row => row.project_id)
      .filter(Boolean);
    return projects.filter(p => !selectedIds.includes(p.id));
  };

  return (
    <DashboardLayout title="Timesheets">
      <div className="space-y-6" data-testid="timesheets-page">
        <Tabs defaultValue="entry" className="space-y-6">
          <TabsList data-testid="timesheets-tabs">
            <TabsTrigger value="entry" data-testid="tab-entry">Weekly Entry</TabsTrigger>
            <TabsTrigger value="overview" data-testid="tab-overview">Effort Overview</TabsTrigger>
          </TabsList>

          <TabsContent value="entry" className="space-y-6 mt-0">
            {/* Week Navigation */}
            <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
                data-testid="prev-week-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="text-center">
                <h3 className="font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {format(weekStart, "MMM d")} - {format(weekEnd, "MMM d, yyyy")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Week Total: <span className="data-cell font-medium">{getWeekTotal()}h</span>
                </p>
              </div>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
                data-testid="next-week-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Timesheet Grid */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="section-title">Weekly Timesheet</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={addProjectRow}
                disabled={projects.length === 0}
                data-testid="add-row-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Project
              </Button>
              <Button
                onClick={saveTimesheets}
                disabled={saving || weeklyData.length === 0}
                data-testid="save-timesheet-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">Loading timesheet...</div>
              </div>
            ) : projects.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Clock className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No active projects</h3>
                <p className="text-muted-foreground mt-1">Create a project first to start tracking time</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 table-header min-w-[200px]">Project</th>
                      <th className="text-left py-3 px-2 table-header min-w-[120px]">Client</th>
                      {weekDays.map((day) => (
                        <th key={day.toISOString()} className="text-center py-3 px-2 table-header min-w-[80px]">
                          <div>{format(day, "EEE")}</div>
                          <div className="text-xs">{format(day, "d")}</div>
                        </th>
                      ))}
                      <th className="text-center py-3 px-2 table-header min-w-[80px]">Total</th>
                      <th className="py-3 px-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyData.map((row, rowIndex) => (
                      <tr key={row.id ?? `new-${row.project_id || 'p'}-${rowIndex}`} className="border-b hover:bg-accent/30 transition-colors">
                        <td className="py-2 px-2">
                          {row.isNew ? (
                            <Select
                              value={row.project_id}
                              onValueChange={(value) => updateProjectSelection(rowIndex, value)}
                            >
                              <SelectTrigger className="w-full" data-testid={`project-select-${rowIndex}`}>
                                <SelectValue placeholder="Select project" />
                              </SelectTrigger>
                              <SelectContent>
                                {getAvailableProjects(rowIndex).map((project) => (
                                  <SelectItem key={project.id} value={project.id}>
                                    {project.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="font-medium">{row.project_name}</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-muted-foreground text-sm">
                          {row.client_name || "-"}
                        </td>
                        {weekDays.map((day) => {
                          const dateStr = format(day, "yyyy-MM-dd");
                          const dayData = row.hours[dateStr] || { hours: 0 };
                          return (
                            <td key={dateStr} className="py-2 px-1">
                              <Input
                                type="number"
                                min="0"
                                max="24"
                                step="0.5"
                                value={dayData.hours || ""}
                                onChange={(e) => updateHours(rowIndex, dateStr, e.target.value)}
                                className="w-16 text-center data-cell h-9"
                                placeholder="0"
                                disabled={!row.project_id}
                                data-testid={`hours-${rowIndex}-${dateStr}`}
                              />
                            </td>
                          );
                        })}
                        <td className="py-2 px-2 text-center">
                          <span className="data-cell font-medium">{getTotalForRow(row)}h</span>
                        </td>
                        <td className="py-2 px-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => removeProjectRow(rowIndex)}
                            data-testid={`remove-row-${rowIndex}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {/* Daily totals row */}
                    <tr className="bg-muted/50 font-medium">
                      <td className="py-3 px-2" colSpan={2}>Daily Total</td>
                      {weekDays.map((day) => {
                        const dateStr = format(day, "yyyy-MM-dd");
                        return (
                          <td key={dateStr} className="py-3 px-2 text-center">
                            <span className="data-cell">{getTotalForDay(dateStr)}h</span>
                          </td>
                        );
                      })}
                      <td className="py-3 px-2 text-center">
                        <span className="data-cell text-primary font-bold">{getWeekTotal()}h</span>
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="overview" className="mt-0">
            <EffortOverview />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
